import { type JSX, type ReactNode, useState } from "react";
import ReactCountryFlag from "react-country-flag";

import { CustomProxyForm } from "common/components/Proxy";
import { Button } from "common/components/ui/Button";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import { Tag } from "common/components/ui/Tag";
import { decodeProxyUrl } from "common/helpers/utils";
import type { CustomProxy } from "common/types";

import DeleteProxy from "../DeleteProxy";

type Action = "edit" | "delete";

interface WebhookMobilePreviewProps {
  proxy: CustomProxy;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

function ProxyItem({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-x-4 py-2 border-b border-b-black-200 -mx-4 px-4">
      <h5 className="text-black-500">{title}</h5>
      {children}
    </div>
  );
}

export default function MobilePreview({
  proxy,
  isOpen,
  setIsOpen,
}: WebhookMobilePreviewProps) {
  const [action, setAction] = useState<Action>(null);

  let mainComponent: JSX.Element;

  if (proxy) {
    const { name, url, location, valid: isValid } = proxy;
    const { protocol, address, password, username } = decodeProxyUrl(url);
    mainComponent = (
      <>
        <DrawerHeader title={name} />
        <DrawerBody>
          <section>
            <ProxyItem title="Url">
              <p className="text-button-sm">{`${protocol}://${address}`}</p>
            </ProxyItem>

            <ProxyItem title="Authentication">
              <p className="text-button-sm">
                {username && password ? `${username}:${password}` : "-"}
              </p>
            </ProxyItem>

            <ProxyItem title="Location">
              <p className="text-button-sm">
                {location ? (
                  <Tag variant="tertiary">
                    <ReactCountryFlag countryCode={location.countryISO} />
                    {location.city || location.country}
                  </Tag>
                ) : (
                  "-"
                )}
              </p>
            </ProxyItem>

            <ProxyItem title="Status">
              {isValid ? (
                <Tag variant="success" className="mr-auto">
                  Valid
                </Tag>
              ) : (
                <Tag variant="disabled" className="mr-auto">
                  Invalid
                </Tag>
              )}
            </ProxyItem>
          </section>
        </DrawerBody>

        <DrawerFooter className="items-center justify-between">
          <Button variant="outline-danger" onClick={() => setAction("delete")}>
            Delete
          </Button>
          <Button onClick={() => setAction("edit")}>Edit</Button>
        </DrawerFooter>
      </>
    );
  }

  if (action === "delete") {
    mainComponent = (
      <DeleteProxy
        proxy={proxy}
        onBack={() => setAction(null)}
        onSuccess={() => setIsOpen(false)}
      />
    );
  } else if (action === "edit") {
    mainComponent = (
      <CustomProxyForm proxy={proxy} onBack={() => setAction(null)} />
    );
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={setIsOpen}
      onAfterClose={() => setAction(null)}
    >
      <DrawerContent>{mainComponent}</DrawerContent>
    </Drawer>
  );
}
